import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations, renderImages } from '../data/mock';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';

const RendersGallery = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(renderImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % renderImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(renderImages[nextIndex]);
  };

  const goToPrev = () => {
    const prevIndex = (currentIndex - 1 + renderImages.length) % renderImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(renderImages[prevIndex]);
  };

  // First 8 images for initial grid display
  const displayImages = renderImages.slice(0, 8);

  return (
    <section id="renders" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 mb-4">
            {t.renders.title}
          </h2>
          <p className="text-stone-500 text-lg">
            {t.renders.subtitle}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {displayImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => openLightbox(index)}
              className={`relative overflow-hidden group cursor-pointer ${
                index === 0 ? 'col-span-2 row-span-2' : ''
              }`}
            >
              <div className={`${index === 0 ? 'aspect-square' : 'aspect-square'}`}>
                <img
                  src={image.url}
                  alt={image.alt[language]}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              <div className="absolute bottom-2 left-2 right-2 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-2 py-1 truncate">
                {image.alt[language]}
              </div>
            </button>
          ))}
        </div>

        {/* View All Button */}
        {renderImages.length > 8 && (
          <div className="text-center mt-8">
            <button
              onClick={() => openLightbox(0)}
              className="text-stone-600 hover:text-amber-700 text-sm tracking-wider uppercase transition-colors border border-stone-300 px-6 py-3 hover:border-amber-700"
            >
              {language === 'es' ? `Ver todas las imágenes (${renderImages.length})` : `View all images (${renderImages.length})`}
            </button>
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-5xl w-full bg-black/95 border-none p-0">
            <div className="relative w-full aspect-[4/3]">
              {selectedImage && (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt[language]}
                  className="w-full h-full object-contain"
                />
              )}
              
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>

              {/* Navigation */}
              <button
                onClick={goToPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-2"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-2"
              >
                <ChevronRight size={32} />
              </button>

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
                {currentIndex + 1} / {renderImages.length}
              </div>

              {/* Image caption */}
              {selectedImage && (
                <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white text-sm">
                  {selectedImage.alt[language]}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default RendersGallery;
